import { Component, Input } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { ConfirmationService, MessageService } from 'primeng/api';
import { AppService } from '../../../services/app.service';
import { UtilsService } from '../../../services/utils.service';
import { AuthService } from '../../../services/auth.service';
import { UserService } from '../../../services/users.service';
import { VirtualCardsService } from '../../../services/virtual_cards.service';
import { BitgoService } from '../../../services/bitgo.service';
import { DomSanitizer } from '@angular/platform-browser';
import { PaymentDeclarationsService } from '../../../services/payment_declarations.service';

const VIRTUAL_CARD_RECHARGE_CURRENCY = 'USDT';

@Component({
  selector: 'app-view-virtual-card',
  templateUrl: './view-virtual-card.component.html',
  styleUrl: './view-virtual-card.component.scss',
})
export class ViewVirtualCardComponent {
  @Input() card: any = null;

  showCardDetails = false;
  updatingCard = false;
  loadingCardBalance = false;
  showProviderPan = false;
  panNumber: string | null = null;

  showCardWallets = false;

  selectedNetwork: any = null;
  selectedToken: any = null;

  showRechargeCard = false;
  rechargingCard = false;
  loadingRechargeFee = false;
  rechargeFeeError = '';
  rechargeFee: {
    amount: string;
    fee_amount: string;
    net_amount: string;
    currency: string;
  } | null = null;
  rechargeWallets: Array<{
    id: string;
    user_subwallet_id?: number | null;
    shortname: string;
    balance: number;
    wallet_shortname: string;
  }> = [];
  loadingRechargeWallets = false;
  rechargeForm = this.formBuilder.group({
    amount: [null as number | null, Validators.required],
    wallet_id: [null as any, Validators.required],
  });
  private rechargeFeeRequestId = 0;

  constructor(
    private cardsService: VirtualCardsService,
    private formBuilder: FormBuilder,
    public utilsService: UtilsService,
    private confirmationService: ConfirmationService,
    private messageService: MessageService,
    public auth: AuthService,
    public appService: AppService,
    public userService: UserService,
    private sanitizer: DomSanitizer,
    private paymentDeclaration: PaymentDeclarationsService,
    private bitgoService: BitgoService,
  ) {}

  ngOnInit() {
    this.card.PAN = null;
    this.card.balance = null;
    this.card.wallets = null;
    this.showCardDetails = true;

    if (this.card.status === 'DELETED') {
      return;
    }

    this.cardsService.getCardPAN(this.card.id).then(
      (resp: any) => {
        const data = resp?.data || {};
        this.panNumber =
          data.pan || data.card_number || data.number || null;

        if (data.url) {
          this.card.PAN = this.sanitizer.bypassSecurityTrustResourceUrl(
            data.url,
          );
        }
      },
      () => {
        if (this.isLuxCapitalBrand) {
          this.messageService.add({
            severity: 'warn',
            summary: 'Aviso',
            detail:
              'No se pudieron cargar el número y el CVV. Puedes ver el diseño de la tarjeta.',
          });
          return;
        }

        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail:
            'Ocurrió un error al cargar la información de tu tarjeta, por seguridad intenta nuevamente.',
        });
        this.exitView();
      },
    );

    this.loadingCardBalance = true;
    this.cardsService.getCardBalance(this.card.id).then(
      (resp: any) => {
        this.loadingCardBalance = false;
        this.card.balance = resp.data.available_credit;
      },
      () => {
        this.loadingCardBalance = false;
      },
    );
    if (this.userService.hasPlatformService('virtual-card:manage')) {
      this.cardsService.getCardWallets(this.card.id).then(
        (resp: any) => {
          this.card.wallets = resp.data[0];
        },
        () => {},
      );
    }

    /* this.card.wallets = [
      {
        blockchain: 'POLYGON',
        tokens: [
          {
            address: '0x98aEc7277E222F1bFb15Ac6E019cBB5b5b33f7f5',
            symbol: 'USDT',
            name: 'Tether USD',
            blockchain: 'POLYGON',
          },
        ],
      },
    ]; */
  }

  get isLuxCapitalBrand(): boolean {
    return this.auth.getCardBrand(this.card) === 'luxcapital';
  }

  get displayNumber(): string {
    if (this.panNumber) {
      return String(this.panNumber)
        .replace(/\D/g, '')
        .replace(/(.{4})/g, '$1 ')
        .trim();
    }

    const last4 = this.card?.last4 || this.card?.last_four_digits || '';
    return last4 ? `**** **** **** ${last4}` : '**** **** **** ****';
  }

  toggleProviderPan() {
    this.showProviderPan = !this.showProviderPan;
  }

  exitView() {
    this.appService.triggerViewVirtualCard(false);
  }

  openTransactions() {
    this.appService.triggerViewVirtualCardTransactions(true, this.card);
  }

  confirmFreezeCard(event: Event) {
    if (this.updatingCard) return;

    let message =
      this.card.status == 'ACTIVE'
        ? `
        Aviso: Al congelar esta tarjeta, se bloqueará temporalmente su uso y 
        <br>
        no se podrán realizar transacciones hasta que sea descongelada.
        <br><br>
      
        ¿Estás seguro de querer congelar tu tarjeta?
      `
        : `Aviso: Al descongelar esta tarjeta, se restablecerá su uso normal 
        <br>y podrá realizar transacciones nuevamente.
        <br><br>
      ¿Estás seguro de querer congelar tu tarjeta?`;
    let confirm =
      this.card.status == 'ACTIVE' ? 'Si, congelar.' : 'Si, descongelar.';

    this.confirmationService.confirm({
      target: event.target as EventTarget,
      message: message,
      header: 'Confirmación',
      icon: undefined,
      acceptIcon: 'none',
      acceptLabel: confirm,
      rejectIcon: 'none',
      rejectButtonStyleClass: 'p-button-text',
      accept: () => {
        this.updatingCard = true;
        this.cardsService.changeCardStatus(this.card.id, this.card.status).then(
          (resp: any) => {
            this.card.status =
              this.card.status == 'ACTIVE' ? 'FROZEN' : 'ACTIVE';
            this.updatingCard = false;
            this.appService.triggerRefreshVirtualCards();
            this.messageService.add({
              severity: 'success',
              summary: 'Listo',
              detail: 'Hemos modificado el status de tu tarjeta exitosamente.',
            });
          },
          () => {
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: 'Ocurrió un error al modificar el status de tu tarjeta.',
            });
            this.updatingCard = false;
          },
        );
      },
    });
  }

  toggleWallets() {
    if (!this.auth.guardTransactionalAction()) return;
    this.appService.triggerViewWallets(
      true,
      'virtual_card',
      null,
      'USDT',
      this.card.wallets,
    );
  }

  openRechargeCard() {
    if (!this.auth.guardTransactionalAction()) return;
    if (this.card?.status !== 'ACTIVE') {
      this.messageService.add({
        severity: 'warn',
        summary: 'Aviso',
        detail: 'Solo se pueden recargar tarjetas activas.',
      });
      return;
    }

    this.rechargeForm.reset();
    this.rechargeFee = null;
    this.rechargeFeeError = '';
    this.showRechargeCard = true;
    this.loadRechargeWallets();
  }

  closeRechargeCard() {
    this.showRechargeCard = false;
    this.rechargingCard = false;
    this.rechargeForm.reset();
    this.rechargeFee = null;
    this.rechargeFeeError = '';
  }

  onRechargeAmountChange(value?: number | string | null) {
    const amount = Number(value ?? this.rechargeForm.controls.amount.value);
    if (!amount || amount <= 0) {
      this.rechargeFee = null;
      this.rechargeFeeError = '';
      this.loadingRechargeFee = false;
      return;
    }
    this.loadRechargeFee(amount);
  }

  canSubmitRecharge(): boolean {
    return (
      this.rechargeForm.valid &&
      !this.rechargingCard &&
      !this.loadingRechargeFee &&
      !this.rechargeFeeError &&
      this.rechargeFee != null &&
      Number(this.rechargeFee.net_amount) > 0
    );
  }

  formattedRechargeAmount(value: string | number | null | undefined): string {
    const amount = Number(value);
    if (!Number.isFinite(amount)) return '';
    return `${amount.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 8,
    })} ${VIRTUAL_CARD_RECHARGE_CURRENCY}`;
  }

  selectedRechargeWalletLabel(): string {
    const wallet = this.rechargeForm.controls.wallet_id.value;
    return wallet?.wallet_shortname || wallet?.shortname || '';
  }

  confirmRechargeCard(event: Event) {
    if (!this.canSubmitRecharge() || !this.rechargeFee) return;

    this.confirmationService.confirm({
      target: event.target as EventTarget,
      message: `
        Se debitarán <b>${this.formattedRechargeAmount(this.rechargeFee.amount)}</b>
        desde ${this.selectedRechargeWalletLabel() || 'el wallet seleccionado'}
        y se enviarán a la tarjeta. TransferSwap no cobra fee de recarga.
        <br><br>
        ¿Confirmas la recarga?
      `,
      header: 'Confirmar recarga',
      icon: undefined,
      acceptIcon: 'none',
      acceptLabel: 'Si, recargar.',
      rejectIcon: 'none',
      rejectButtonStyleClass: 'p-button-text',
      accept: () => this.submitRecharge(),
    });
  }

  private submitRecharge() {
    if (!this.canSubmitRecharge() || !this.rechargeFee) return;
    if (!this.auth.guardTransactionalAction()) return;

    const selectedWallet = this.rechargeForm.controls.wallet_id.value;
    this.rechargingCard = true;
    this.cardsService
      .rechargeCard(this.card.id, {
        amount: this.rechargeForm.controls.amount.value as number,
        ...(selectedWallet?.user_subwallet_id
          ? { user_subwallet_id: selectedWallet.user_subwallet_id }
          : { wallet_id: selectedWallet?.id ?? selectedWallet }),
      })
      .then((resp: any) => {
        this.rechargingCard = false;
        this.appService.triggerRefreshVirtualCards();
        this.closeRechargeCard();
        this.messageService.add({
          severity: 'success',
          summary: 'Listo',
          detail: `Enviamos ${this.formattedRechargeAmount(resp?.data?.net_amount)} a CryptoMate. El saldo de la tarjeta se acredita cuando confirme la red.`,
        });
      })
      .catch((resp: any) => {
        const code = String(resp?.code || '');
        const backendMessage = String(resp?.error || resp?.message || '');
        const message =
          code === 'VIRTUAL_CARD_INSUFFICIENT_BALANCE' ||
          backendMessage.includes('Insufficient')
            ? 'Saldo de USDT insuficiente en el wallet seleccionado para recargar la tarjeta.'
            : code === 'VIRTUAL_CARD_WALLET_NOT_FOUND' ||
                backendMessage.includes('wallet_id')
              ? 'El wallet seleccionado no es válido para recargar la tarjeta.'
              : code === 'VIRTUAL_CARD_PRICING_ERROR'
                ? backendMessage || 'No se pudo calcular el monto de recarga.'
                : code === 'VIRTUAL_CARD_TOPUP_BITGO_FAILED' ||
                    code === 'VIRTUAL_CARD_TOPUP_NETWORK_MISMATCH'
                  ? backendMessage ||
                    'No se pudo enviar el monto a la address de recarga de CryptoMate.'
                  : backendMessage || 'Ocurrió un error al recargar la tarjeta.';
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: message,
        });
        this.rechargingCard = false;
      });
  }

  private loadRechargeFee(amount: number) {
    const requestId = ++this.rechargeFeeRequestId;
    this.loadingRechargeFee = true;
    this.rechargeFeeError = '';
    this.cardsService
      .getRechargeFee(this.card.id, amount)
      .then((response: any) => {
        if (requestId !== this.rechargeFeeRequestId) return;
        const data = response?.data ?? response;
        const netAmount = Number(data?.net_amount);
        if (!Number.isFinite(netAmount) || netAmount <= 0) {
          this.rechargeFeeError =
            'El monto de recarga no es válido.';
          this.rechargeFee = null;
          this.loadingRechargeFee = false;
          return;
        }
        this.rechargeFee = {
          amount: String(data.amount),
          fee_amount: String(data.fee_amount),
          net_amount: String(data.net_amount),
          currency: String(data.currency || VIRTUAL_CARD_RECHARGE_CURRENCY),
        };
        this.loadingRechargeFee = false;
      })
      .catch((error: any) => {
        if (requestId !== this.rechargeFeeRequestId) return;
        this.rechargeFee = null;
        this.rechargeFeeError =
          error?.error ||
          error?.message ||
          'No se pudo consultar el monto de recarga.';
        this.loadingRechargeFee = false;
      });
  }

  private loadRechargeWallets() {
    const sessionUserId = this.auth.user?.user_id;
    this.loadingRechargeWallets = true;
    this.bitgoService
      .listWallets(sessionUserId, { tree: false, includeBalances: true })
      .subscribe(
        (res) => {
          this.loadingRechargeWallets = false;
          const cryptoWallets = this.mapCryptoWallets(res?.data ?? res);
          this.rechargeWallets = cryptoWallets.length
            ? cryptoWallets
            : this.getUsdtWallets();
        },
        () => {
          this.loadingRechargeWallets = false;
          this.rechargeWallets = this.getUsdtWallets();
        },
      );
  }

  private getUsdtWallets() {
    const subwallets =
      this.userService.user_balance_config?.currencies?.find(
        (currency: any) => String(currency?.id || '').toUpperCase() === 'USDT',
      )?.subwallets ?? [];

    return subwallets
      .filter((wallet: any) => wallet?.id)
      .map((wallet: any, index: number) => {
        const balance = Number(wallet.balance || 0);
        const shortname = wallet.shortname || `Wallet ${index + 1}`;
        return {
          id: String(wallet.id),
          shortname,
          balance,
          wallet_shortname: `${shortname} — ${balance} USDT`,
        };
      });
  }

  private mapCryptoWallets(data: any) {
    const rows = Array.isArray(data?.wallets)
      ? data.wallets
      : Array.isArray(data?.roots)
        ? data.roots
        : Array.isArray(data)
          ? data
          : [];
    const usdtNetworks = new Set(['polygon', 'tron', 'ofc']);
    return rows
      .filter((wallet: any) => {
        const coin = String(wallet?.coin || '').toUpperCase();
        const network = String(wallet?.network || '').toLowerCase();
        return coin === 'USDT' || coin === 'USDC' || usdtNetworks.has(network);
      })
      .map((wallet: any) => {
        const label =
          wallet.label ||
          wallet.network ||
          wallet.bitgo_wallet_id ||
          `Wallet ${wallet.user_subwallet_id}`;
        const balance = Number(wallet.ledger_balance ?? wallet.balance ?? 0);
        return {
          id: String(
            wallet.bitgo_wallet_id ||
              wallet.subwallet_id ||
              wallet.user_subwallet_id,
          ),
          user_subwallet_id: wallet.user_subwallet_id ?? null,
          shortname: label,
          balance,
          wallet_shortname: `${label} — ${balance} USDT`,
        };
      });
  }

  selectNetwork(network: any) {
    this.selectedNetwork = network;
  }

  selectToken(token: any) {
    this.selectedToken = token;
    this.toggleShowDeclarePayment();
  }

  toggleShowDeclarePayment() {
    if (!this.auth.guardTransactionalAction()) return;
    this.appService.triggerAddPayment(
      true,
      this.selectedToken.blockchain,
      this.selectedToken.symbol,
      'virtual_card',
      null,
      this.selectedToken.wallet,
    );
  }
}
